import React, { useState, useEffect } from 'react';
import { adminInspectionService } from '../../services/adminService';
import UpdatePricingModal from './workflow/UpdatePricingModal';

const PricingManagement = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminInspectionService.getAllInspections();
      
      console.log('API Response:', response);
      
      const allInspections = response.data || response.car_inspections || [];
      
      console.log('All Inspections:', allInspections);
      
      const pricingRelevantInspections = allInspections.filter(inspection => {
        const hasReport = inspection.inspection_report !== null && inspection.inspection_report !== undefined;
        const isScheduledOrCompleted = inspection.status === 'scheduled' || inspection.status === 'completed' || inspection.status === 'priced';
        
        console.log(`Inspection #${inspection.id}:`, {
          hasReport,
          status: inspection.status,
          isScheduledOrCompleted,
          include: hasReport && isScheduledOrCompleted
        });
        
        return hasReport && isScheduledOrCompleted;
      });
      
      console.log('Pricing Relevant Inspections:', pricingRelevantInspections);
      
      setInspections(pricingRelevantInspections);
    } catch (error) {
      console.error('Error loading inspections:', error);
      setError('Failed to load inspections: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetPricing = (inspection) => {
    console.log('Setting pricing for inspection:', inspection);
    setSelectedInspection(inspection);
    setShowPricingModal(true);
  };

  const handlePricingSuccess = () => {
    setShowPricingModal(false);
    setSelectedInspection(null);
    loadInspections();
  };

  const getPricingStats = () => {
    const notPriced = inspections.filter(i => !i.estimated_price).length;
    const priced = inspections.filter(i => i.estimated_price).length;
    const totalValue = inspections
      .filter(i => i.estimated_price)
      .reduce((sum, i) => sum + parseFloat(i.estimated_price || 0), 0);

    return { notPriced, priced, totalValue };
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'not_priced' && !inspection.estimated_price) ||
      (filterStatus === 'priced' && inspection.estimated_price);
    
    const matchesSearch = 
      inspection.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.sell_request?.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.sell_request?.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.id?.toString().includes(searchTerm);
    
    return matchesFilter && matchesSearch;
  });

  const stats = getPricingStats();

  if (loading) {
    return (
      <div className="management-loading">
        <div className="spinner-large"></div>
        <p>Loading pricing data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-loading">
        <p style={{ color: '#dc3545' }}>{error}</p>
        <button className="refresh-btn" onClick={loadInspections}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pricing-management">
      {/* Header */}
      <div className="management-header">
        <div>
          <h1>💰 Pricing Management</h1>
          <p>Set and manage pricing for completed inspections</p>
        </div>
        <button className="refresh-btn" onClick={loadInspections}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.2 3.7 15.4 2.8 13.5 2.3C9.5 1.3 5.5 2.9 3.5 6.3C1.5 9.7 1.8 14 4.5 17M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.8 20.3 8.6 21.2 10.5 21.7C14.5 22.7 18.5 21.1 20.5 17.7C22.5 14.3 22.2 10 19.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="pricing-stats">
        <div className="stat-card pricing-stat">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
              <path d="M12 6V18M9 9H12C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15H9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Pending Pricing</h3>
            <div className="stat-value">{stats.notPriced}</div>
            <p className="stat-subtitle">Inspections need pricing</p>
          </div>
        </div>

        <div className="stat-card priced-stat">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 11L12 14L22 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Priced</h3>
            <div className="stat-value">{stats.priced}</div>
            <p className="stat-subtitle">₹{stats.totalValue.toLocaleString()} Total Value</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="management-filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by ID, booking ref, or car..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All
            <span className="filter-count">{inspections.length}</span>
          </button>
          <button
            className={`filter-tab ${filterStatus === 'not_priced' ? 'active' : ''}`}
            onClick={() => setFilterStatus('not_priced')}
          >
            Not Priced
            <span className="filter-count">{stats.notPriced}</span>
          </button>
          <button
            className={`filter-tab ${filterStatus === 'priced' ? 'active' : ''}`}
            onClick={() => setFilterStatus('priced')}
          >
            Priced
            <span className="filter-count">{stats.priced}</span>
          </button>
        </div>
      </div>

      {/* Inspections Table */}
      <div className="management-table-wrapper" style={{ overflowX: 'auto' }}>
        <div className="management-table">
          <table style={{ tableLayout: 'auto', width: '100%', minWidth: '750px' }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>ID</th>
                <th style={{ whiteSpace: 'nowrap' }}>Booking Ref</th>
                <th>Car Details</th>
                <th>Seller</th>
                <th>Score</th>
                <th style={{ whiteSpace: 'nowrap' }}>Current Price</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInspections.map((inspection) => (
                <tr key={inspection.id}>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>#{inspection.id}</strong></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <code style={{ fontSize: '10px' }}>{inspection.booking_reference}</code>
                  </td>
                  <td>
                    <div className="car-details">
                      <h4>{inspection.sell_request?.brand} {inspection.sell_request?.model}</h4>
                      <p>{inspection.sell_request?.year} • {inspection.sell_request?.km_driven?.toLocaleString()} km</p>
                    </div>
                  </td>
                  <td>
                    <div>
                      <p style={{ fontSize: '11px', margin: 0 }}>{inspection.seller?.name || 'N/A'}</p>
                      <small style={{ fontSize: '10px', color: '#666' }}>{inspection.seller?.phone || '-'}</small>
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inspection.inspection_report ? (
                      <div>
                        <strong style={{ color: '#28a745', fontSize: '16px' }}>
                          {inspection.inspection_report.overall_score}/10
                        </strong>
                        {inspection.inspection_report.flooded && (
                          <div><small style={{ color: '#dc3545', fontSize: '10px' }}>⚠️ Flooded</small></div>
                        )}
                        {inspection.inspection_report.meter_tampered && (
                          <div><small style={{ color: '#dc3545', fontSize: '10px' }}>⚠️ Tampered</small></div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>N/A</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inspection.estimated_price ? (
                      <strong style={{ color: '#28a745', fontSize: '13px' }}>
                        ₹{parseFloat(inspection.estimated_price).toLocaleString()}
                      </strong>
                    ) : (
                      <span style={{ color: '#ffc107', fontWeight: '600', fontSize: '11px' }}>Not Priced</span>
                    )}
                  </td>
                  <td>
                    {inspection.overall_rating ? (
                      <span className="grade-badge">{inspection.overall_rating}</span>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ 
                        background: inspection.estimated_price ? '#28a74520' : '#ffc10720',
                        color: inspection.estimated_price ? '#28a745' : '#ffc107'
                      }}
                    >
                      {inspection.estimated_price ? 'PRICED' : inspection.status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {!inspection.estimated_price ? (
                        <button 
                          className="btn-price-now"
                          onClick={() => handleSetPricing(inspection)}
                          title="Set Pricing"
                        >
                          💰 Set Price
                        </button>
                      ) : (
                        <button 
                          className="btn-update-price"
                          onClick={() => handleSetPricing(inspection)}
                          title="Update Pricing"
                        >
                          🔄 Update
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredInspections.length === 0 && (
            <div className="no-results">
              <p>No inspections found for pricing</p>
              {inspections.length === 0 && (
                <small>Make sure inspections have completed inspection reports</small>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && selectedInspection && (
        <UpdatePricingModal
          inspection={selectedInspection}
          onClose={() => {
            setShowPricingModal(false);
            setSelectedInspection(null);
          }}
          onSuccess={handlePricingSuccess}
        />
      )}
    </div>
  );
};

export default PricingManagement;